import { Injectable } from '@angular/core'

import { BehaviorSubject, map, shareReplay, takeUntil } from 'rxjs'

import { Action, DataQueryProps, Resource, Tsp, UserProfile } from '@ti/contracts'
import { AccountsListScope } from '@ti/core/api/scopes/ti/routes/accounts.route'
import { DataGridColumnActionsFn, DataGridColumnType } from '@ti/core/contracts/data-grid'
import { ListModelConfig } from '@ti/core/models/types'

import { BaseAccountsModel } from '../../common/models/base-accounts.model'

export interface TspGridView extends Tsp {
  isDemo: boolean
  owner: UserProfile | null
}

export const ACCOUNTS_TSP_SCOPE_PARAM = 'tspId'

/**
 * Rendered untranslated, exactly like the grid column it labels. Kept in one place so the column
 * header and the scope indicator cannot drift apart.
 */
export const TSP_ID_LABEL = 'TSP ID'

/**
 * Exact-id scope taken from the route. `requested` keeps what the link asked for even when that
 * value is unusable: a malformed link has to be reported, never widened back to every account.
 */
export interface AccountsScope {
  readonly requested: string | null
  readonly tspId: number | null
}

export const NO_ACCOUNTS_SCOPE: AccountsScope = { requested: null, tspId: null }

/**
 * Only a positive safe integer is a usable account id. Everything else - empty, signed, decimal,
 * exponential, non-numeric or past the safe-integer range - stays rejected, because the textual
 * `search` this scope replaces matches an id as a substring and would list 142 for a link to 42.
 */
export const parseAccountsScope = (raw: string | null): AccountsScope => {
  if (raw === null) {
    return NO_ACCOUNTS_SCOPE
  }

  const candidate = raw.trim()
  const tspId = /^\d+$/.test(candidate) ? Number(candidate) : Number.NaN

  return {
    requested: raw,
    tspId: Number.isSafeInteger(tspId) && tspId > 0 ? tspId : null,
  }
}

export const isRejectedAccountsScope = (scope: AccountsScope): boolean =>
  scope.requested !== null && scope.tspId === null

const toListScope = (scope: AccountsScope): AccountsListScope =>
  scope.tspId === null ? {} : { tspId: scope.tspId }

@Injectable()
export class AccountsModel extends BaseAccountsModel<TspGridView> {
  public readonly scope$ = new BehaviorSubject<AccountsScope>(NO_ACCOUNTS_SCOPE)

  /**
   * The scope narrows the same result set as search does, so an empty scoped page is a filtered
   * empty state rather than an account directory that has never had a TSP in it.
   */
  public override get isSearchOrFilterApplied() {
    const { requested } = this.scope$.value
    return requested === null ? super.isSearchOrFilterApplied : ACCOUNTS_TSP_SCOPE_PARAM
  }

  /**
   * Applied from the route before the model's first page/count load. A search remembered from an
   * earlier visit narrows the same query and can hide the linked account, so the link wins.
   */
  public applyScope(scope: AccountsScope): void {
    if (scope.requested === this.scope$.value.requested) {
      return
    }

    if (scope.requested !== null) {
      this.setSearch('')
    }

    this.scope$.next(scope)
    this.store.loadedPages.clear()
    this.store.page$.next(0)
    this.reloadCount()
  }

  public async selectById(id: number): Promise<TspGridView> {
    const account = await this.api.ti.accounts.one(id)
    const view = {
      ...account,
      isDemo: !account.billingStartDate,
      owner: null,
    }
    this.select(view)

    return view
  }

  protected config(): ListModelConfig {
    return {
      name: 'AccountsModel',
      retainCacheUrl: [''],
      defaultOrderColumn: 'createdAt',
      defaultOrderDirection: 'DESC',
      gridColumns: [
        {
          field: 'name',
          label: 'ti-admin.accounts.fields.name',
          type: DataGridColumnType.Template,
          sortable: true,
        },
        {
          field: 'billingStartDate',
          label: 'ti-admin.accounts.fields.type',
          type: DataGridColumnType.Template,
          sortable: true,
        },
        {
          field: 'id',
          label: TSP_ID_LABEL,
          type: DataGridColumnType.Text,
          sortable: true,
        },
        {
          field: 'legalName',
          label: 'ti-admin.accounts.fields.legal-name',
          type: DataGridColumnType.Text,
          sortable: true,
        },
        {
          field: 'devicesCount',
          label: 'ti-admin.accounts.fields.devices-count',
          type: DataGridColumnType.Text,
          sortable: false,
        },
        {
          field: 'createdAt',
          label: 'ti-admin.accounts.fields.created-at',
          format: 'grid-date',
          type: DataGridColumnType.Date,
          sortable: true,
        },
        {
          field: 'billingMode',
          label: 'ti-admin.accounts.fields.billing-mode',
          type: DataGridColumnType.Template,
          sortable: true,
        },
        {
          field: 'isActive',
          label: 'ti-admin.accounts.fields.suspended',
          type: DataGridColumnType.Template,
          sortable: true,
        },
        {
          field: 'loginAs',
          label: '',
          type: DataGridColumnType.Template,
          sortable: false,
        },
        {
          field: 'actions',
          label: '',
          type: DataGridColumnType.Actions,
          actions: ((data: TspGridView) => {
            return this.accessControl
              .massCheck$([
                [Resource.TSP, Action.Update],
                [Resource.TSP, Action.Delete],
                [Resource.Billing, Action.View],
              ])
              .pipe(
                map(([canUpdate, canDelete, canViewBilling]) => {
                  return [
                    {
                      label: 'ti-admin.accounts.page.edit-btn',
                      icon: 'pi pi-pencil',
                      styleClass: `${canUpdate ? '' : 'not-allowed'}`,
                      // rollback once billing moved to separate section
                      // styleClass: `has-bottom-border ${canUpdate ? '' : 'not-allowed'}`,
                      command: () => (canUpdate ? this.openEditDialog(data) : undefined),
                    },
                    {
                      label: 'Last Billing Month Details',
                      icon: 'pi pi-wallet',
                      styleClass: `has-bottom-border ${canViewBilling ? '' : 'not-allowed'}`,
                      command: () => {
                        if (!canViewBilling) {
                          return
                        }
                        const year = new Date().getUTCFullYear()
                        const month = new Date().getUTCMonth()
                        return this.router.navigate(['/billing', data.id, year, month])
                      },
                    },
                    {
                      label: 'ti-admin.accounts.page.delete-btn',
                      icon: 'pi pi-trash',
                      styleClass: `error ${canDelete ? '' : 'not-allowed'}`,
                      command: () => (canDelete ? this.confirmDelete(data) : undefined),
                    },
                  ]
                }),
                takeUntil(this.destroy$),
                shareReplay({ bufferSize: 1, refCount: false }),
              )
          }) as DataGridColumnActionsFn,
        },
      ],
    }
  }

  protected async loadPage(props: DataQueryProps): Promise<TspGridView[]> {
    const scope = this.scope$.value
    if (isRejectedAccountsScope(scope)) {
      return []
    }

    const data = await this.api.ti.accounts.list(props, toListScope(scope))

    return data.map((tsp) => ({
      ...tsp,
      isDemo: !tsp.billingStartDate,
      owner: tsp.owner ?? null,
    }))
  }

  protected async loadCount(props: DataQueryProps) {
    const scope = this.scope$.value
    if (isRejectedAccountsScope(scope)) {
      return 0
    }

    return this.api.ti.accounts.count({ search: props.search }, toListScope(scope))
  }
}
